<script lang="ts">
  import { TokenStream } from '$lib/commands/lexer';
  import { Parser } from '$lib/commands/parser';
  import type { BaseCommand, CommandResult } from '$lib/commands/BaseCommand';
  import type { ActionItem } from '$lib/commands/SearchCommand';

  let cmdString = $state('');
  let isLoading = $state(false);
  let cmdOptions = $state<any[]>([]);
  let showAutocomplete = $state(false);
  let selectedIndex = $state(-1);
  let selectedActionIndex = $state(-1);
  let commandMessages = $state<string[]>([]);
  let currentCommand = $state<BaseCommand | null>(null);
  let actionItems = $state<ActionItem[]>([]);
  let { onAction = () => {}, inputRef = $bindable() }: { onAction?: () => void; inputRef?: HTMLInputElement } = $props();

  function onMessage(message: string) {
    commandMessages = [...commandMessages, message];
  }

  function parseOptions() {
    try {
      const tokens = new TokenStream(cmdString).tokenize();
      const parser = new Parser(tokens);
      cmdOptions = parser.parse();
    } catch (e: unknown) {
      if (e instanceof Error && !e.message.startsWith('Invalid command')) {
        throw e;
      }
      cmdOptions = [];
    }
  }

  function autocompleteSelection(cmdOption: BaseCommand) {
    cmdString = `${cmdOption.getCommand()} `;
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === '"') {
      const selection = window.getSelection()?.toString();
      if (selection) {
        cmdString = cmdString.replace(selection, `"${selection}"`);
        e.preventDefault();
      }
    } else if (e.key === 'Enter' && e.metaKey) {
      handleCommandEnter();
      e.preventDefault();
    } else if (actionItems.length > 0) {
      if (e.key === 'ArrowDown') {
        selectedActionIndex = Math.min(selectedActionIndex + 1, actionItems.length - 1);
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        selectedActionIndex = Math.max(selectedActionIndex - 1, 0);
        e.preventDefault();
      } else if (e.key === 'Enter' && selectedActionIndex >= 0) {
        const selectedItem = actionItems[selectedActionIndex];
        executeAction(selectedItem, new MouseEvent('click', { metaKey: e.metaKey }));
        e.preventDefault();
      }
    }
  }

  function onKeyup(e: KeyboardEvent) {
    if (actionItems.length > 0) {
      if (e.key === 'Escape') {
        actionItems = [];
        commandMessages = [];
        selectedActionIndex = -1;
      }
      return;
    }

    if (e.key === 'ArrowDown' && !currentCommand) {
      selectedIndex = Math.min(selectedIndex + 1, cmdOptions.length - 1);
      autocompleteSelection(cmdOptions[selectedIndex]);
    } else if (e.key === 'ArrowUp' && !currentCommand) {
      selectedIndex = Math.max(selectedIndex - 1, 0);
      autocompleteSelection(cmdOptions[selectedIndex]);
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0 && !currentCommand) {
        autocompleteSelection(cmdOptions[selectedIndex]);
      }
      showAutocomplete = false;
      return;
    } else if (e.key === 'Escape') {
      showAutocomplete = false;
      actionItems = [];
      selectedActionIndex = -1;
    } else {
      selectedIndex = -1;
    }

    if (!currentCommand) {
      parseOptions();
      showAutocomplete = cmdOptions.length > 0;
    }
  }

  function onSubmit(e: Event) {
    e.preventDefault();
    parseOptions();
    if (cmdOptions.length !== 1) {
      return;
    }
    commandMessages = [];
    actionItems = [];
    selectedActionIndex = -1;
    isLoading = true;
    showAutocomplete = false;
    currentCommand = cmdOptions[0];

    if (!currentCommand) {
      isLoading = false;
      return;
    }

    const actionPromise = currentCommand.action([], onMessage);

    if (actionPromise instanceof Promise) {
      actionPromise
        .then((itemList: CommandResult) => {
          isLoading = false;
          if (itemList) {
            actionItems = itemList as ActionItem[];
            if (actionItems.length > 0) {
              selectedActionIndex = 0;
            }
          } else {
            onAction();
          }
        })
        .catch((e: Error) => {
          isLoading = false;
          let errMsg = e.message;
          try {
            const parsed = JSON.parse(e.message);
            if (parsed && typeof parsed === 'object' && parsed.message) {
              errMsg = parsed.message;
            }
          } catch {
            // use original e.message
          }
          onMessage(errMsg);
        });
    } else {
      isLoading = false;
      onAction();
    }
  }

  function executeAction(item: ActionItem, e: MouseEvent) {
    item.action(e, onAction);
  }

  function handleCommandEnter() {
    if (selectedActionIndex >= 0 && actionItems.length > selectedActionIndex) {
      const selectedItem = actionItems[selectedActionIndex];
      executeAction(selectedItem, new MouseEvent('click', { metaKey: true }));
    }
  }
</script>

<form onsubmit={onSubmit} class="w-full">
  <div class="relative w-full">
    <div class="w-full">
      <input
        type="text"
        bind:value={cmdString}
        placeholder="Type to start: 'user', 'create user', 'revenue', 'search'"
        bind:this={inputRef}
        onkeyup={onKeyup}
        onkeydown={onKeydown}
        class="w-full text-lg px-4 py-3 rounded-lg border border-gray-300 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
        class:opacity-60={isLoading}
        disabled={isLoading}
      />
    </div>
    {#if showAutocomplete}
      <div class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
        {#each cmdOptions as cmdOption, i}
          <div
            class="px-4 py-3 text-sm border-b border-gray-100 last:border-b-0"
            class:bg-primary-50={i === selectedIndex}
          >
            <p>{@html cmdOption.preview()}</p>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</form>

{#if commandMessages.length > 0}
  <div class="mt-4 max-h-72 overflow-y-auto">
    {#each commandMessages as message}
      <div class="rounded-md bg-blue-50 border border-blue-200 p-3 mb-2">
        <p class="text-sm font-medium text-blue-800 mb-1">Command</p>
        <div class="text-sm text-blue-700">{@html message}</div>
      </div>
    {/each}
  </div>
{/if}

{#if actionItems.length > 0}
  <div class="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-white">
    <div class="bg-gray-100 px-4 py-3 border-b border-gray-200">
      <p class="font-bold text-gray-800 text-sm mb-0.5">Search Results</p>
      <small class="text-gray-500 text-xs">Use arrow keys to navigate, Enter to select, or Cmd+Enter to open in new tab</small>
    </div>
    <div class="max-h-72 overflow-y-auto">
      {#each actionItems as item, i}
        <button
          onclick={(e) => executeAction(item, e)}
          class="w-full text-left px-4 py-3 text-sm text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors cursor-pointer"
          class:bg-primary-500={i === selectedActionIndex}
          class:text-white={i === selectedActionIndex}
          class:bg-white={i !== selectedActionIndex}
          class:hover:bg-gray-50={i !== selectedActionIndex}
        >
          {item.message}
        </button>
      {/each}
    </div>
  </div>
{/if}

<style>
  :global(.cmd_typed) {
    font-weight: 700;
    color: #f7941d;
  }

  :global(.tag) {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    line-height: 1.2;
    white-space: nowrap;
  }

  :global(.tag.is-light) {
    background-color: #f3f4f6;
    color: #374151;
  }

  :global(.tag.is-success) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.has-text-primary) {
    color: #f7941d;
  }

  :global(.cmd) {
    color: #9ca3af;
  }
</style>
